import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./Legal.css";
import { API_BASE_URL } from "../config";

export default function TermsAndConditions() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [sections, setSections] = useState([]);
  const [activeSection, setActiveSection] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);

    fetch(`${API_BASE_URL}/api/legal/active?type=terms`)
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then((json) => {
        if (!json.success) throw new Error();
        setData(json.data);

        // Parse content into sections by splitting on H2/numbered headings
        const parsed = parseContentToSections(json.data.content);
        setSections(parsed);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  // Scroll spy
  useEffect(() => {
    const handleScroll = () => {
      sections.forEach((_, i) => {
        const el = document.getElementById(`terms-section-${i}`);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120) setActiveSection(i);
        }
      });
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [sections]);

  if (loading) {
    return (
      <div className="legal-page">
        <div className="legal-loading">
          <div className="legal-spinner" />
          <p>Loading Terms & Conditions...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="legal-page">
        <div className="legal-error">
          <span style={{ fontSize: "3rem" }}>📋</span>
          <h2>Terms Not Available</h2>
          <p>Our Terms & Conditions are being updated. Please check back shortly.</p>
          <Link to="/" className="legal-cta-btn" style={{ marginTop: 8 }}>
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="legal-page">
      {/* ── HERO ── */}
      <section className="legal-hero">
        <div className="legal-hero-badge">📋 Legal Document</div>
        <h1>{data.title}</h1>
        <p>
          Please read these terms carefully before using FlyTutor's services,
          platforms, or training programs.
        </p>
        <div className="legal-meta">
          {/* <div className="legal-meta-item">
            <span>Version</span>
            <span>v{data.version}</span>
          </div> */}
          <div className="legal-meta-item">
            <span>Last Updated</span>
            <span>
              {new Date(data.updatedAt).toLocaleDateString("en-IN", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </span>
          </div>
          <div className="legal-meta-item">
            <span>Effective</span>
            <span>Immediately</span>
          </div>
        </div>
      </section>

      {/* ── MAIN LAYOUT ── */}
      <div className="legal-layout">
        {/* Sidebar TOC */}
        <aside className="legal-sidebar">
          <div className="legal-toc-card">
            <h3>Contents</h3>
            <ul className="legal-toc-list">
              {sections.map((sec, i) => (
                <li key={i}>
                  <a
                    href={`#terms-section-${i}`}
                    className={activeSection === i ? "active" : ""}
                    onClick={(e) => {
                      e.preventDefault();
                      document
                        .getElementById(`terms-section-${i}`)
                        ?.scrollIntoView({ behavior: "smooth" });
                    }}
                  >
                    {sec.heading}
                  </a>
                </li>
              ))}
            </ul>
            <Link to="/privacy-policy" className="legal-other-link">
              🔒 Privacy Policy →
            </Link>
          </div>
        </aside>

        {/* Content */}
        <main className="legal-content-area">
          {sections.length > 0 ? (
            sections.map((sec, i) => (
              <div
                key={i}
                id={`terms-section-${i}`}
                className="legal-section-block"
              >
                <h2>
                  <span className="section-num">{i + 1}</span>
                  {sec.heading}
                </h2>
                <div
                  className="legal-section-body"
                  dangerouslySetInnerHTML={{ __html: sec.body }}
                />
              </div>
            ))
          ) : (
            // Fallback: render full content as-is
            <div
              className="legal-section-body"
              dangerouslySetInnerHTML={{ __html: data.content }}
            />
          )}
        </main>
      </div>

      {/* ── BOTTOM CTA ── */}
      <section className="legal-cta-section">
        <h3>Questions about our Terms?</h3>
        <p>Our team is happy to clarify anything you need.</p>
        <Link to="/contact" className="legal-cta-btn">
          Contact Us
        </Link>
      </section>
    </div>
  );
}

/**
 * Splits raw content into sections.
 * Handles HTML with <h2> tags OR plain text with numbered headings like "1. Title".
 */
function parseContentToSections(content) {
  const isHTML = /<[a-z][\s\S]*>/i.test(content);

  if (isHTML) {
    // Split by <h2> tags
    const parts = content.split(/(?=<h2[^>]*>)/i);
    return parts
      .filter((p) => p.trim())
      .map((part) => {
        const headingMatch = part.match(/<h2[^>]*>(.*?)<\/h2>/i);
        if (!headingMatch) return { heading: "Overview", body: part };
        const heading = headingMatch[1].replace(/<[^>]+>/g, "").trim();
        const body = part.replace(/<h2[^>]*>.*?<\/h2>/i, "").trim();
        return { heading, body };
      });
  }

  // Plain text: split on numbered headings like "1. Heading" or "## Heading"
  const lines = content.split("\n");
  const sections = [];
  let current = null;

  lines.forEach((line) => {
    const numbered = line.match(/^(\d+)\.\s+(.+)/);
    const hashed = line.match(/^#{1,2}\s+(.+)/);

    if (numbered || hashed) {
      if (current) sections.push(current);
      current = {
        heading: numbered ? numbered[2] : hashed[1],
        body: "",
      };
    } else if (current) {
      current.body += line + "\n";
    } else {
      // Content before first heading
      if (!sections[0]) {
        current = { heading: "Introduction", body: line + "\n" };
      }
    }
  });

  if (current) sections.push(current);

  // Convert plain text body to <p> tags
  return sections.map((s) => ({
    heading: s.heading,
    body: s.body
      .split(/\n\n+/)
      .filter((p) => p.trim())
      .map((p) => `<p>${p.replace(/\n/g, " ").trim()}</p>`)
      .join(""),
  }));
}